/* eslint-disable react/jsx-wrap-multilines, react/jsx-one-expression-per-line */
import React, { lazy, Suspense } from 'react';

// Redux
import { useDispatch } from 'react-redux';

// Router DOM
import { useHistory } from 'react-router-dom';

// Material
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

// Material Icons

// Constants
import CONST_PAYMENT from 'constants/payments';
import IMAGES from 'constants/images';

// Types
import { TripInterface } from 'store/ducks/trips/types';

// Actions
import { setSecondOpen } from 'store/ducks/dialogs/actions';

// Helpers
import { currencyFormat } from 'helpers/currencyFormat';

// Styles
import useStyle from './informationNavigation.styles';

interface StateProps {
  tripData: TripInterface;
}

type Props = StateProps;

// Lazy Componenents

const TripReportDialog = lazy(() =>
  import('components/TripReportDialog/TripReportDialog'),
);

const InformationNavigation: React.FC<Props> = ({ tripData }) => {
  const classes = useStyle();
  const history = useHistory();
  const dispatch = useDispatch();

  return (
    <div className={classes.root}>
      <Box display='flex' flexDirection='column' width='100%'>
        {tripData?.LineSection && (
          <Box m={2}>
            <Box>
              <Typography variant='h6' color='primary'>
                Informações da Viagem
              </Typography>
            </Box>
            <List
              disablePadding
              component='nav'
              aria-label='entries informations'
              className={classes.list}>
              <ListItem
                disableGutters
                alignItems='flex-start'
                className={classes.listItem}>
                {tripData.Car?.license && (
                  <ListItemText
                    primary={`Carro: ${tripData.Car?.license} - ${tripData.Car?.prefix}`}
                  />
                )}

                {tripData.LineSection.Line?.name && (
                  <ListItemText
                    primary={`Linha: ${tripData.LineSection.Line?.name}`}
                  />
                )}

                {tripData.Dbd?.serial && (
                  <ListItemText primary={`Dbd: ${tripData.Dbd?.serial}`} />
                )}
              </ListItem>
            </List>
          </Box>
        )}
        <Divider />
        {tripData?.EntriesResume && (
          <Box m={2}>
            <Box>
              <Typography variant='h6' color='primary'>
                Informações de Passagens
              </Typography>
            </Box>
            <List
              disablePadding
              component='nav'
              aria-label='entries resume'
              className={classes.list}>
              <ListItem
                disableGutters
                alignItems='flex-start'
                className={classes.listItem}>
                <ListItemText
                  primary={`Quantidade de Passagens: ${tripData.EntriesResume.quantity}`}
                />
                <ListItemText
                  primary={`Total Transacionado em Passagens na Viagem: ${currencyFormat(
                    {
                      value: tripData.EntriesResume.totalValue,
                    },
                  )}`}
                />
              </ListItem>
            </List>
            <Box>
              <TableContainer className={classes.table} component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell variant='head'>Entrada</TableCell>
                      <TableCell variant='head' align='center'>
                        Valor
                      </TableCell>
                      <TableCell variant='head' align='center'>
                        Passagens
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tripData?.EntriesResume?.entries?.payments &&
                      Object.entries(
                        tripData.EntriesResume.entries.payments,
                      ).map(
                        ([key, content]: [
                          string,
                          { value: number; quantity: number },
                        ]) => (
                          <TableRow key={key}>
                            <TableCell>
                              {
                                CONST_PAYMENT.ENTRY.find(
                                  (PAYMENT_TYPE) => PAYMENT_TYPE.KEY === key,
                                )?.VALUE
                              }
                            </TableCell>
                            <TableCell align='center'>
                              {currencyFormat({
                                value: content.value,
                              })}
                            </TableCell>
                            <TableCell align='center'>
                              {content.quantity}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
        <Divider />
        {tripData?.EntriesResume && (
          <Box m={2}>
            <Box>
              <Typography variant='h6' color='primary'>
                Informações de Venda do Motorista
              </Typography>
            </Box>
            <List
              disablePadding
              component='nav'
              aria-label='driver resume'
              className={classes.list}>
              <ListItem
                disableGutters
                alignItems='flex-start'
                className={classes.listItem}>
                <ListItemText
                  primary={`Total Vendido na Viagem: ${currencyFormat({
                    value: tripData.EntriesResume.transactionedValue,
                  })}`}
                />
                <ListItemText
                  primary={`Total Pago: ${currencyFormat({
                    value: tripData.EntriesResume.paidValue,
                  })}`}
                />
                <ListItemText
                  primary={`Total em Aberto: ${currencyFormat({
                    value: tripData.EntriesResume.owned,
                  })}`}
                />
              </ListItem>
            </List>
          </Box>
        )}
      </Box>
      <Box display='flex' flexDirection='column' width='100%'>
        <span className={classes.registrationDate}>
          Data de registro:{' '}
          {tripData?.createdAt
            ? new Date(tripData?.createdAt).toLocaleString('pt-BR')
            : ''}
        </span>
        <Divider variant='fullWidth' />
        <Box flexGrow={0} p={2}>
          <Box alignContent='center' display='flex' flexDirection='row'>
            <Box>
              <Tooltip title='Laudo da Viagem' placement='top'>
                <IconButton
                  onClick={() => {
                    dispatch(
                      setSecondOpen({
                        open: true,
                        maxWidth: 'xl',
                        content: (
                          <Suspense fallback={<p>Carregando...</p>}>
                            <TripReportDialog
                              tripId={tripData?.id ? tripData?.id : ''}
                            />
                          </Suspense>
                        ),
                      }),
                    );
                  }}>
                  <img
                    style={{ width: 20 }}
                    alt='icon'
                    src={IMAGES.ICONS.TRIP_REPORT}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export { InformationNavigation };
export default InformationNavigation;
